import React, { useEffect, useMemo, useState } from 'react';
import axiosInstance from '../axiosConfig';


const LeavePolicyAssignCard = ({
  leaves,
  loading,
  onAssigned,
  disabled,
}) => {
  const [branchId, setBranchId] = useState('');
  const [branches, setBranches] = useState([]);

  const [leaveTypeId, setLeaveTypeId] = useState('');

  const [rateMode, setRateMode] = useState('monthly');
  const [monthlyDays, setMonthlyDays] = useState();
  const [yearlyDays, setYearlyDays] = useState();

  const [carryForward, setCarryForward] = useState(true);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        // API: /office-branch/
        const res = await axiosInstance.get('/office-branch/');
        setBranches(res.data || []);
      } catch (err) {
        // Keep silent to avoid breaking UI; user can still select leave type
        // eslint-disable-next-line no-console
        console.error('Failed to fetch office branches:', err);
      }
    };

    fetchBranches();
  }, []);

  const selectedLeave = useMemo(() => {

    if (!leaveTypeId) return null;
    return (leaves || []).find((l) => String(l.id) === String(leaveTypeId)) || null;
  }, [leaves, leaveTypeId]);

  const code = selectedLeave?.code || '';
  const name = selectedLeave?.name || '';

  const payload = useMemo(() => {
    const base = {
      branch_id: branchId ? Number(branchId) : undefined,
      code,
      name,
      rate_mode: rateMode,
      carry_forward: carryForward,
    };

    if (rateMode === 'monthly') {
      base.monthly_days = Number(monthlyDays) || 0;
    } else {
      base.yearly_days = Number(yearlyDays) || 0;
    }

    // keep only defined keys (esp branch_id)
    return Object.fromEntries(Object.entries(base).filter(([, v]) => v !== undefined));
  }, [branchId, code, name, rateMode, monthlyDays, yearlyDays, carryForward]);

  const handleAssign = async () => {
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      if (!payload.branch_id) {
        setError('Please select an office branch');
        return;
      }
      if (!leaveTypeId) {
        setError('Please select a leave type');
        return;
      }
      if (!payload.code || !payload.name) {
        setError('Leave type info missing');
        return;
      }

      await axiosInstance.post('/leave-policy-assign/', payload);
      setSuccess('Leave policy assigned successfully');
      setSubmitting(false);
      if (onAssigned) onAssigned();
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          err.response?.data ||
          err.message ||
          'Failed to assign leave policy'
      );
      setSubmitting(false);
    }
  };

  return (
    <div className="dashboard-card">
      <div className="card-header">
        <span className="card-icon">📌</span>
        <h3>Assign Leaves to Office Branch</h3>
      </div>

      <div className="leave-policy-card-body">
        {error && (
          <div className="leave-request-error" style={{ marginBottom: 12 }}>
            <span className="error-icon">⚠️</span>
            <span>{String(error)}</span>
          </div>
        )}

        {success && (
          <div className="leave-request-success" style={{ marginBottom: 12 }}>
            <span className="success-icon">✓</span>
            <span>{success}</span>
          </div>
        )}

        <div className="leave-policy-form">
          <div className="leave-policy-grid">
            <div className="form-group">
              <label htmlFor="branchId">Office Branch</label>
              <select
                id="branchId"
                className="form-input"
                value={branchId}
                onChange={(e) => setBranchId(e.target.value)}
                disabled={disabled}
              >
                <option value="">Select branch</option>
                {(branches || []).map((b) => (
                  <option key={b.id ?? b.branch_id ?? b.code} value={b.id ?? b.branch_id ?? ''}>
                    {b.name || b.office_branch_name || b.branch_name || b.code || b.id}
                  </option>
                ))}

              </select>
              <small style={{ color: '#64748b' }}>Provide branch_id</small>
            </div>

            <div className="form-group">
              <label htmlFor="leaveTypeId">Leave Type</label>
              <select
                id="leaveTypeId"
                className="form-input"
                value={leaveTypeId}
                onChange={(e) => setLeaveTypeId(e.target.value)}
                disabled={disabled}
              >
                <option value="">Select leave type</option>
                {(leaves || []).map((lt) => (
                  <option key={lt.id} value={lt.id}>
                    {lt.name} ({lt.code})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="rateMode">Rate Mode</label>
              <select
                id="rateMode"
                className="form-input"
                value={rateMode}
                onChange={(e) => setRateMode(e.target.value)}
                disabled={disabled}
              >
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>

            {rateMode === 'monthly' ? (
              <div className="form-group">
                <label htmlFor="monthlyDays">Monthly Days</label>
                <input
                  id="monthlyDays"
                  type="number"
                  step="1"
                  className="form-input"
                  value={monthlyDays}
                  onChange={(e) => setMonthlyDays(e.target.value)}
                  disabled={disabled}
                />
              </div>
            ) : (
              <div className="form-group">
                <label htmlFor="yearlyDays">Yearly Days</label>
                <input
                  id="yearlyDays"
                  type="number"
                  step="1"
                  className="form-input"
                  value={yearlyDays}
                  onChange={(e) => setYearlyDays(e.target.value)}
                  disabled={disabled}
                />
              </div>
            )}

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={carryForward}
                  onChange={(e) => setCarryForward(e.target.checked)}
                  disabled={disabled}
                />
                <span className="checkbox-custom" />
                <span className="checkbox-text">Carry forward</span>
              </label>
            </div>

            <div className="form-group" />
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="submit-leave-btn"
              onClick={handleAssign}
              disabled={submitting || disabled}
            >
              {submitting ? 'Assigning...' : 'Assign Leaves'}
            </button>
          </div>

          {/* hidden debug */}
          <div style={{ display: 'none' }}>
            <pre>{JSON.stringify(payload, null, 2)}</pre>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeavePolicyAssignCard;

